import RestoreIcon from '@mui/icons-material/Restore';
import { Alert, Button, Chip, Paper, Stack } from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatBoxPieces } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import type { InactiveProductStockRow } from '@tiles-erp/shared-types';
import { PageContainer } from '@tiles-erp/ui';
import { PERMISSIONS } from '@tiles-erp/config';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useCatalogOptions } from '../catalog/api';
import { useBranches } from '../products/branch-prices-api';
import { useProductSizes, useReactivateProduct } from '../products/api';
import { useGodowns, useInactiveProductStock } from './api';

export function InactiveProductStockReportPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const canActivate = hasPermission(PERMISSIONS.PRODUCT_UPDATE);
  const reactivate = useReactivateProduct();
  const pagination = usePagination({ initialPageSize: 100 });
  const [branchId, setBranchId] = useState('');
  const [godownId, setGodownId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const branches = useBranches();
  const godowns = useGodowns(branchId || undefined);
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const filters = {
    branchId: branchId || undefined, godownId: godownId || undefined,
    brandId: brandId || undefined, categoryId: categoryId || undefined,
    sizeMm: sizeMm || undefined,
  };
  const report = useInactiveProductStock(pagination.query, filters);
  const columns = useMemo<ColDef<InactiveProductStockRow>[]>(() => [
    { field: 'sku', headerName: 'SKU', minWidth: 110, pinned: 'left' },
    { field: 'productName', headerName: 'Product', minWidth: 230, pinned: 'left' },
    { field: 'status', headerName: 'Status', minWidth: 105,
      cellRenderer: (p: { value?: string }) => <Chip size="small" color="error" label={p.value === 'DELETED' ? 'Deleted' : 'Inactive'} /> },
    { field: 'brandName', headerName: 'Brand', minWidth: 145 },
    { field: 'categoryName', headerName: 'Category', minWidth: 175 },
    { field: 'sizeMm', headerName: 'Size', minWidth: 90 },
    { field: 'sixorbitId', headerName: 'SixOrbit ID', minWidth: 120 },
    { field: 'branchName', headerName: 'Branch', minWidth: 190 },
    { field: 'godownName', headerName: 'Godown', minWidth: 155 },
    { headerName: 'Available stock', minWidth: 150,
      valueGetter: (p) => p.data ? formatBoxPieces(p.data.qtyBoxes, p.data.piecesPerBox,
        p.data.baseUom === 'PIECE' || p.data.piecesPerBox === 1) : '' },
    { field: 'qtyPieces', headerName: 'Stock pcs', minWidth: 115,
      valueFormatter: (p) => Number(p.value ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 }) },
    ...(canActivate ? [{
      headerName: 'Action', minWidth: 125, sortable: false, filter: false,
      cellRenderer: (p: ICellRendererParams<InactiveProductStockRow>) => p.data ? (
        <Button size="small" variant="outlined" startIcon={<RestoreIcon />}
          disabled={reactivate.isPending}
          onClick={() => reactivate.mutate(p.data!.productId)}>
          Activate
        </Button>
      ) : null,
    } satisfies ColDef<InactiveProductStockRow>] : []),
  ], [canActivate, reactivate]);

  return <PageContainer title="Inactive products with stock"
    subtitle="Products hidden from normal selection that still have a positive stock balance by branch and godown.">
    <Stack spacing={1.5}>
      {report.error && <Alert severity="error">{report.error.message}</Alert>}
      {reactivate.error && <Alert severity="error">{reactivate.error.message}</Alert>}
      {reactivate.isSuccess && <Alert severity="success">Product activated and restored to the Product list.</Alert>}
      <Alert severity="warning">These products still hold stock. Review and merge or reactivate them before removing their master records.</Alert>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <SearchableSelect label="Branch" value={branchId} allLabel="All branches"
            options={(branches.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setBranchId(value); setGodownId(''); pagination.setPage(1); }} width={230} />
          <SearchableSelect label="Godown" value={godownId} allLabel="All godowns"
            options={(godowns.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setGodownId(value); pagination.setPage(1); }} width={210} />
          <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
            options={(brands.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setBrandId(value); setCategoryId(''); pagination.setPage(1); }} width={210} />
          <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
            options={(categories.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setCategoryId(value); pagination.setPage(1); }} width={230} />
          <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
            options={(sizes.data ?? []).map((row) => ({ value: row, label: row }))}
            onChange={(value) => { setSizeMm(value); pagination.setPage(1); }} width={150} />
        </Stack>
      </Paper>
      <DataTable rows={report.data?.items ?? []} columns={columns} meta={report.data?.meta}
        pagination={pagination} loading={report.isFetching}
        searchPlaceholder="Search SKU, product or SixOrbit ID…"
        exportTitle="Inactive products with stock" exportFilename="inactive-products-with-stock" height={650} />
    </Stack>
  </PageContainer>;
}
